/**
 * Re-encode an image File through a canvas so any EXIF orientation the
 * browser applied during decode is baked into the pixel data and the
 * resulting bytes carry no EXIF tag.
 *
 * Why: iOS phone photos are stored landscape with an EXIF "rotate" flag.
 * The browser honors that flag when rendering <img>, but downstream
 * consumers (Gemini's image-generation model in particular) read the raw
 * pixel buffer and ignore EXIF, so AI-generated edits come out rotated
 * 90° relative to the user's view of their input. Normalizing here means
 * Storage, the chat view, the design board, the PDF, and Gemini all see
 * the same orientation.
 *
 * Non-image Files (PDFs, etc.) and any decode/encode failure pass the
 * original file through unchanged.
 */
export async function bakeImageOrientation(file) {
  if (!file || typeof file.type !== 'string' || !file.type.startsWith('image/')) {
    return file;
  }
  try {
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('Image decode failed'));
      i.src = dataUrl;
    });
    const width = img.naturalWidth || 0;
    const height = img.naturalHeight || 0;
    if (!width || !height) return file;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx || typeof canvas.toBlob !== 'function') return file;
    ctx.drawImage(img, 0, 0);

    const isPng = file.type === 'image/png';
    const outputType = isPng ? 'image/png' : 'image/jpeg';
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
        outputType,
        isPng ? undefined : 0.92
      );
    });

    const ext = isPng ? '.png' : '.jpg';
    const base = (file.name || 'image').replace(/\.(jpe?g|png|heic|heif|webp)$/i, '');
    const newName = base + ext;
    return new File([blob], newName, { type: outputType, lastModified: file.lastModified });
  } catch {
    return file;
  }
}
